import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, MessageSquare, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Inbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/me/conversations");
      setConvs(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { if (user) loadConvs(); }, [user, loadConvs]);
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(loadConvs, 15000);
    return () => clearInterval(t);
  }, [user, loadConvs]);

  const openConv = async (c) => {
    setActive(c);
    setThread(null);
    try {
      const { data } = await api.get(`/me/conversations/${c.id}`);
      setThread(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
      loadConvs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/me/conversations/${active.id}/reply`, { body: draft });
      const { data } = await api.get(`/me/conversations/${active.id}`);
      setThread(data);
      setDraft("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
      loadConvs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="pt-28 md:pt-32" data-testid="inbox-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <p className="mb-3 text-xs uppercase tracking-luxe text-velvet-gold">Live inbox</p>
          <h1 className="font-heading text-4xl font-semibold leading-none tracking-tight text-white md:text-6xl">
            Your <span className="italic text-velvet-rose">conversations</span>
          </h1>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-[320px_1fr] md:gap-0">
          {/* Threads list */}
          <aside className={`border border-white/10 ${active ? "hidden md:block" : "block"}`}>
            <div className="border-b border-white/10 px-5 py-4 text-[10px] uppercase tracking-luxe text-white/40">
              {convs.length} thread{convs.length !== 1 ? "s" : ""}
            </div>
            {convs.length === 0 ? (
              <p className="p-8 text-center text-xs uppercase tracking-luxe text-white/30">No messages yet</p>
            ) : (
              <ul className="max-h-[70vh] overflow-y-auto" data-testid="conv-list">
                {convs.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => openConv(c)}
                      data-testid={`conv-item-${c.id}`}
                      className={`flex w-full items-center gap-3 border-b border-white/10 px-5 py-4 text-left transition-colors hover:bg-white/[0.03] ${
                        active?.id === c.id ? "bg-white/[0.04]" : ""
                      }`}
                    >
                      {c.listing_image && (
                        <img src={c.listing_image} alt="" className="h-12 w-10 flex-shrink-0 object-cover" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center justify-between text-sm text-white">
                          <span className="truncate">{c.other_name}</span>
                          <span className="ml-2 flex-shrink-0 text-[10px] text-white/40">{timeAgo(c.last_activity)}</span>
                        </p>
                        <p className="mt-0.5 truncate text-xs font-light text-white/50">{c.last_message || "…"}</p>
                        <p className="mt-1 truncate text-[10px] uppercase tracking-luxe text-velvet-gold">re: {c.listing_name}</p>
                      </div>
                      {c.unread > 0 && (
                        <span className="rounded-full bg-velvet-rose px-2 py-0.5 text-[10px] text-white">{c.unread}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Thread view */}
          <section className={`border border-white/10 md:border-l-0 ${active ? "block" : "hidden md:block"}`}>
            {!active || !thread ? (
              <div className="flex h-[70vh] flex-col items-center justify-center gap-3 text-white/30">
                <MessageSquare size={40} />
                <p className="text-xs uppercase tracking-luxe">Select a conversation</p>
              </div>
            ) : (
              <div className="flex h-[70vh] flex-col">
                <div className="flex items-center gap-3 border-b border-white/10 px-6 py-4">
                  <button onClick={() => setActive(null)} className="text-white/60 md:hidden" data-testid="conv-back">
                    <ChevronLeft size={18} />
                  </button>
                  {active.listing_image && (
                    <img src={active.listing_image} alt="" className="h-10 w-8 object-cover" />
                  )}
                  <div>
                    <p className="text-sm text-white">{active.other_name}</p>
                    <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">re: {active.listing_name}</p>
                  </div>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-6" data-testid="conv-messages">
                  <AnimatePresence initial={false}>
                    {thread.messages.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <motion.div
                          key={m.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[75%] px-4 py-2.5 text-sm ${
                            mine ? "bg-velvet-rose text-black" : "border border-white/10 bg-white/[0.03] text-white"
                          }`}>
                            <p className="whitespace-pre-wrap">{m.body}</p>
                            <p className={`mt-1 text-[9px] uppercase tracking-luxe ${mine ? "text-black/50" : "text-white/40"}`}>
                              {timeAgo(m.created_at)} ago
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  <div ref={endRef} />
                </div>

                <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-4" data-testid="conv-reply-form">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type a reply…"
                    data-testid="conv-reply-input"
                    className="flex-1 border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
                    maxLength={2000}
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    data-testid="conv-reply-send"
                    className="flex items-center gap-2 bg-white px-5 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white disabled:opacity-50"
                  >
                    <Send size={14} /> Send
                  </button>
                </form>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
